import React, { useState, useEffect } from 'react';
import { BrandingUtils } from '../../utils/branding';
import {
    Box,
    Typography,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    Stack,
    Chip,
    Card,
    CardContent,
    Alert,
    Snackbar,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Paper,
    Grid,
    IconButton,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    IconMail,
    IconEye,
    IconSend,
    IconSave,
    IconRefresh,
    IconCode,
    IconEdit,
    IconX,
    IconCalendar,
    IconPlus,
    IconTrash,
    IconCopy,
} from '@tabler/icons';
import { useQuery, useMutation, gql } from '@apollo/client';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MainCard from 'ui-component/cards/MainCard';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { Save } from '@mui/icons-material';
import { format } from 'date-fns';

// GraphQL Queries
const GET_CONFIRMATION_TEMPLATES = gql`
    query GetConfirmationTemplates($contextId: String!, $themeId: String!) {
        getConfirmationTemplates(contextId: $contextId, themeId: $themeId) {
            templates {
                workshopId
                workshopDate
                subject
                body
                isHtml
                createdAt
                updatedAt
            }
        }
    }
`;

const GET_EMAIL_VARIABLES = gql`
    query GetEmailVariables($contextId: String!, $templateType: String!) {
        getEmailVariables(contextId: $contextId, templateType: $templateType) {
            name
            description
            example
        }
    }
`;

const GET_FORM_FIELDS = gql`
    query GetFormFields($eventId: String!, $role: ROLE!) {
        form(eventId: $eventId, role: $role) {
            fields {
                name
                label
                type
            }
        }
    }
`;

// GraphQL Mutations
const SAVE_CONFIRMATION_TEMPLATE = gql`
    mutation SaveConfirmationTemplate(
        $contextId: String!
        $themeId: String!
        $workshopId: String!
        $subject: String!
        $body: String!
        $isHtmlMode: Boolean!
    ) {
        saveConfirmationTemplate(
            contextId: $contextId
            themeId: $themeId
            workshopId: $workshopId
            subject: $subject
            body: $body
            isHtmlMode: $isHtmlMode
        )
    }
`;

const SEND_TEST_CONFIRMATION_EMAIL = gql`
    mutation SendTestConfirmationEmail(
        $contextId: String!
        $themeId: String!
        $workshopId: String!
        $testEmail: String!
        $role: String!
    ) {
        sendTestConfirmationEmail(
            contextId: $contextId
            themeId: $themeId
            workshopId: $workshopId
            testEmail: $testEmail
            role: $role
        )
    }
`;

// Confirmation Email Template Manager Component
const ConfirmationEmailTemplateManager = ({
    contextId,
    themeId,
    theme,
    title = 'Setup Confirmation Email Templates',
    eventId,
    role,
    onSave,
    onTest,
}) => {
    // State management
    const [templates, setTemplates] = useState({});
    const [expandedAccordions, setExpandedAccordions] = useState({});
    const [testEmail, setTestEmail] = useState('');
    const [testEmailDialog, setTestEmailDialog] = useState(false);
    const [selectedWorkshopId, setSelectedWorkshopId] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [activeField, setActiveField] = useState('subject'); // Track which field is active
    const [copySnackbar, setCopySnackbar] = useState({ open: false, message: '' }); // For copy notifications

    // GraphQL operations
    const {
        data: templatesData,
        loading: templatesLoading,
        refetch: refetchTemplates,
    } = useQuery(GET_CONFIRMATION_TEMPLATES, {
        variables: { contextId, themeId },
        onCompleted: data => {
            console.log('ConfirmationEmailSetup: Templates data received:', data);
            if (data?.getConfirmationTemplates?.templates) {
                const templatesMap = {};
                data.getConfirmationTemplates.templates.forEach(template => {
                    console.log('Processing template for workshop:', template.workshopId, {
                        subject: template.subject,
                        bodyLength: template.body?.length,
                        isHtml: template.isHtml,
                    });
                    templatesMap[template.workshopId] = {
                        subject: template.subject || '',
                        htmlBody: template.body || '',
                        richTextBody: template.body || '',
                        isHtmlMode: template.isHtml || true,
                    };
                });
                console.log('Setting templates map:', templatesMap);
                setTemplates(templatesMap);
            }
        },
    });

    const { data: variablesData, loading: variablesLoading } = useQuery(GET_EMAIL_VARIABLES, {
        variables: { contextId, templateType: 'confirmation' },
    });

    const { data: formData } = useQuery(GET_FORM_FIELDS, {
        variables: { eventId: contextId, role },
        skip: !contextId || !role,
    });

    const [saveTemplate, { loading: saving }] = useMutation(SAVE_CONFIRMATION_TEMPLATE, {
        onCompleted: data => {
            setSnackbar({
                open: true,
                message: data.saveConfirmationTemplate || 'Template saved successfully',
                severity: 'success',
            });
            if (onSave) onSave();
        },
        onError: error => {
            setSnackbar({
                open: true,
                message: error.message || 'Failed to save template',
                severity: 'error',
            });
        },
    });

    const [sendTestEmail, { loading: testing }] = useMutation(SEND_TEST_CONFIRMATION_EMAIL, {
        onCompleted: data => {
            setSnackbar({
                open: true,
                message: data.sendTestConfirmationEmail || 'Test email sent successfully',
                severity: 'success',
            });
            setTestEmailDialog(false);
        },
        onError: error => {
            setSnackbar({
                open: true,
                message: error.message || 'Failed to send test email',
                severity: 'error',
            });
        },
    });

    // Available variables - combine system variables with form fields
    const systemVariables = variablesData?.getEmailVariables || [];
    const formFields = formData?.form?.fields || [];

    // Create dynamic variables from form fields
    const dynamicVariables = formFields.map(field => ({
        name: field.name,
        description: `Form field: ${field.label || field.name}`,
        example: `Sample ${field.label || field.name}`,
    }));

    // System variables for confirmation emails
    const confirmationSystemVariables = [
        {
            name: 'QR',
            description: 'QR code image for event check-in',
            example: 'QR code will be generated automatically',
        },
        {
            name: 'calendar',
            description: 'Calendar ICS file attachment',
            example: 'Calendar event will be attached automatically',
        },
        {
            name: 'BADGE_URL',
            description: 'BADGE_URL',
            example: 'Badge download URL',
        },
        {
            name: 'socialShareLink',
            description: 'Social share button - generates personalized shareable link',
            example: 'Share on Social Media button will be inserted',
        },
        {
            name: 'ShareGeneratorURL',
            description: 'Share Generator page URL - personalized page where users can upload photos, edit content, and share',
            example: 'https://reception.eventegration.com/s/abc123?eventid=123&role=visitor&userid=456',
        },
    ];

    const variables = [...systemVariables, ...dynamicVariables, ...confirmationSystemVariables];

    // Sample data for preview - only system variables
    const sampleData = {
        QR: 'QR code will be generated automatically',
        calendar: 'Calendar event will be attached automatically',
        ThemeName: theme?.name || 'AI Workshop',
        ThemeDescription: theme?.description || 'Learn about AI and Machine Learning',
        WorkshopDate: '2024-01-15',
        WorkshopTime: '10:00 AM - 12:00 PM',
        WorkshopVenue: 'Conference Room A',
        EventDate: '2024-01-15',
        EventTime: '10:00 AM - 12:00 PM',
        Venue: 'Conference Room A',
    };

    // Handle format toggle for specific workshop
    const handleFormatToggle = (workshopId, event) => {
        const newMode = event.target.checked;
        setTemplates(prev => ({
            ...prev,
            [workshopId]: {
                ...prev[workshopId],
                isHtmlMode: newMode,
                htmlBody: newMode ? prev[workshopId]?.richTextBody || '' : prev[workshopId]?.htmlBody || '',
                richTextBody: !newMode ? prev[workshopId]?.htmlBody || '' : prev[workshopId]?.richTextBody || '',
            },
        }));
    };

    // Insert variable into editor for specific workshop
    const insertVariable = (workshopId, variableName) => {
        const variable = `$${variableName}`;
        const template = templates[workshopId];
        if (!template) return;

        if (activeField === 'subject') {
            setTemplates(prev => ({
                ...prev,
                [workshopId]: {
                    ...prev[workshopId],
                    subject: (prev[workshopId]?.subject || '') + variable,
                },
            }));
        } else if (activeField === 'htmlBody') {
            setTemplates(prev => ({
                ...prev,
                [workshopId]: {
                    ...prev[workshopId],
                    htmlBody: (prev[workshopId]?.htmlBody || '') + variable,
                },
            }));
        } else if (activeField === 'richTextBody') {
            setTemplates(prev => ({
                ...prev,
                [workshopId]: {
                    ...prev[workshopId],
                    richTextBody: (prev[workshopId]?.richTextBody || '') + variable,
                },
            }));
        }
    };

    const copyVariable = async variableName => {
        const variable = `$${variableName}`;
        try {
            await navigator.clipboard.writeText(variable);
            setCopySnackbar({
                open: true,
                message: `Copied ${variable} to clipboard`,
            });
        } catch (err) {
            console.error('Failed to copy: ', err);
            setCopySnackbar({
                open: true,
                message: 'Failed to copy to clipboard',
            });
        }
    };

    // Process template with variables
    const processTemplate = (templateSubject, templateBody, data) => {
        let processedSubject = templateSubject;
        let processedBody = templateBody;

        Object.keys(data).forEach(key => {
            const regex = new RegExp(`\\$${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g');
            processedSubject = processedSubject.replace(regex, data[key]);
            processedBody = processedBody.replace(regex, data[key]);
        });

        return { subject: processedSubject, body: processedBody };
    };

    // Handle save for specific workshop
    const handleSave = async workshopId => {
        const template = templates[workshopId];
        if (!template) return;

        const body = template.isHtmlMode ? template.htmlBody : template.richTextBody;
        await saveTemplate({
            variables: {
                contextId,
                themeId,
                workshopId,
                subject: template.subject,
                body,
                isHtmlMode: template.isHtmlMode || false, // Default to false if not set
            },
        });
    };

    // Handle test email for specific workshop
    const handleSendTest = async workshopId => {
        if (!testEmail.trim()) {
            setSnackbar({
                open: true,
                message: 'Please enter a test email address',
                severity: 'error',
            });
            return;
        }

        setSelectedWorkshopId(workshopId);
        await sendTestEmail({
            variables: {
                contextId,
                themeId,
                workshopId,
                testEmail: testEmail.trim(),
                role,
            },
        });
    };

    // Handle accordion toggle
    const handleAccordionToggle = workshopId => {
        setExpandedAccordions(prev => ({
            ...prev,
            [workshopId]: !prev[workshopId],
        }));
    };

    // Initialize templates for all workshops (only if no saved templates exist)
    useEffect(() => {
        console.log('ConfirmationEmailSetup useEffect triggered:', {
            hasTheme: !!theme,
            hasWorkshopDates: !!theme?.workshopDates,
            templatesLoading,
            currentTemplates: Object.keys(templates),
        });

        if (theme?.workshopDates && !templatesLoading) {
            setTemplates(prevTemplates => {
                console.log('useEffect: Current templates before update:', Object.keys(prevTemplates));

                // Only initialize if no templates exist and we're not loading
                if (Object.keys(prevTemplates).length > 0) {
                    console.log('useEffect: Templates already exist, not overwriting');
                    return prevTemplates; // Don't overwrite existing templates
                }

                console.log('useEffect: Initializing default templates');
                const initialTemplates = {};
                theme.workshopDates.forEach(workshop => {
                    initialTemplates[workshop.id] = {
                        subject: `Confirmation for ${theme.name} - ${format(
                            new Date(workshop.eventDate),
                            'dd MMM yyyy'
                        )}`,
                        htmlBody: `<html><body>
                            <h2>Dear Attendee,</h2>
                            <p>Thank you for confirming your attendance for <strong>$ThemeName</strong>.</p>
                            <p><strong>Workshop Details:</strong></p>
                            <ul>
                                <li><strong>Date:</strong> $WorkshopDate</li>
                                <li><strong>Time:</strong> $WorkshopTime</li>
                                <li><strong>Venue:</strong> $WorkshopVenue</li>
                            </ul>
                            <p>QR Code for check-in: $QR</p>
                            <p>Calendar attachment: $calendar</p>
                            <p>We look forward to seeing you there!</p>
                            <p>Best regards,<br/>${BrandingUtils.getBrandingName()} Team</p>
                        </body></html>`,
                        richTextBody: `Dear Attendee,

Thank you for confirming your attendance for $ThemeName.

Workshop Details:
• Date: $WorkshopDate
• Time: $WorkshopTime
• Venue: $WorkshopVenue

QR Code for check-in: $QR
Calendar attachment: $calendar

We look forward to seeing you there!

Best regards,
${BrandingUtils.getBrandingName()} Team`,
                        isHtmlMode: true,
                    };
                });
                console.log('useEffect: Created initial templates:', Object.keys(initialTemplates));
                return initialTemplates;
            });
        }
    }, [theme?.workshopDates, theme?.name, templatesLoading]);

    if (templatesLoading || variablesLoading) {
        return <LinearProgress />;
    }

    return (
        <MainCard title={title}>
            {/* Available Variables */}
            {variables.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant='h6' gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconMail size={20} />
                        Available Variables
                    </Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                        Click on any variable to insert it into your template:
                    </Typography>
                    <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                        {variables.map((variable, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Chip
                                    label={`$${variable.name}`}
                                    variant='outlined'
                                    size='small'
                                    onClick={() => {
                                        // Find the first workshop to insert into
                                        const firstWorkshop = theme?.workshopDates?.[0];
                                        if (firstWorkshop) {
                                            insertVariable(firstWorkshop.id, variable.name);
                                        }
                                    }}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                        },
                                    }}
                                />
                                <Tooltip title='Copy to clipboard'>
                                    <IconButton
                                        size='small'
                                        onClick={e => {
                                            e.stopPropagation();
                                            copyVariable(variable.name);
                                        }}
                                        sx={{
                                            p: 0.5,
                                            '&:hover': {
                                                bgcolor: 'primary.light',
                                                color: 'white',
                                            },
                                        }}
                                    >
                                        <IconCopy size={14} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}

            {/* Share Generator Button Template */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant='h6' gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconCode size={20} />
                    Share Generator Button Template
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                    Use this HTML template to add a "Share My Experience" button in your email. The $ShareGeneratorURL variable will be automatically replaced with the personalized link.
                </Typography>
                <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2, fontFamily: 'monospace', fontSize: '0.875rem', overflow: 'auto' }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{`<table border=0 cellpadding=0 cellspacing=0 style="border-collapse:separate;mso-table-lspace:0;mso-table-rspace:0;width:auto;margin:20px auto 30px auto"align=center><tr><td style=border-radius:4px;padding:0;cursor:pointer align=center bgcolor=#CC0000><!--[if mso]><v:roundrect xmlns:v=urn:schemas-microsoft-com:vml xmlns:w=urn:schemas-microsoft-com:office:word href="$ShareGeneratorURL"style=height:46px;v-text-anchor:middle;width:200px arcsize=13% strokecolor=#CC0000 fillcolor=#CC0000><w:anchorlock><center style=color:#fff;font-family:Arial,sans-serif;font-size:16px;font-weight:700>SHARE MY EXPERIENCE</center></v:roundrect><![endif]--> <a href="$ShareGeneratorURL"style="background-color:#c00;border-radius:4px;color:#fff;text-decoration:none;font-weight:700;display:inline-block;mso-hide:all;padding:12px 25px;font-size:16px">SHARE MY EXPERIENCE</a></table>`}</pre>
                </Box>
                <Button
                    variant='outlined'
                    size='small'
                    startIcon={<IconCopy size={16} />}
                    onClick={async () => {
                        const buttonHtml = `<table border=0 cellpadding=0 cellspacing=0 style="border-collapse:separate;mso-table-lspace:0;mso-table-rspace:0;width:auto;margin:20px auto 30px auto"align=center><tr><td style=border-radius:4px;padding:0;cursor:pointer align=center bgcolor=#CC0000><!--[if mso]><v:roundrect xmlns:v=urn:schemas-microsoft-com:vml xmlns:w=urn:schemas-microsoft-com:office:word href="$ShareGeneratorURL"style=height:46px;v-text-anchor:middle;width:200px arcsize=13% strokecolor=#CC0000 fillcolor=#CC0000><w:anchorlock><center style=color:#fff;font-family:Arial,sans-serif;font-size:16px;font-weight:700>SHARE MY EXPERIENCE</center></v:roundrect><![endif]--> <a href="$ShareGeneratorURL"style="background-color:#c00;border-radius:4px;color:#fff;text-decoration:none;font-weight:700;display:inline-block;mso-hide:all;padding:12px 25px;font-size:16px">SHARE MY EXPERIENCE</a></table>`;
                        try {
                            await navigator.clipboard.writeText(buttonHtml);
                            setCopySnackbar({
                                open: true,
                                message: 'Button template copied to clipboard!',
                            });
                        } catch (err) {
                            console.error('Failed to copy:', err);
                        }
                    }}
                >
                    Copy Button Template
                </Button>
            </Box>

            {/* Workshop Templates */}
            {theme?.workshopDates?.map(workshop => {
                const template = templates[workshop.id] || {};
                const isExpanded = expandedAccordions[workshop.id];

                return (
                    <Accordion
                        key={workshop.id}
                        expanded={isExpanded}
                        onChange={() => handleAccordionToggle(workshop.id)}
                        sx={{ mb: 2 }}
                    >
                        <AccordionSummary
                            expandIcon={<IconCalendar size={20} />}
                            sx={{
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText',
                                '&:hover': { bgcolor: 'primary.main' },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                <Typography variant='h6'>
                                    {format(new Date(workshop.eventDate), 'dd MMMM yyyy')}
                                </Typography>
                                <Stack direction='row' spacing={1}>
                                    <Chip
                                        icon={<IconCalendar size={16} />}
                                        label={`${workshop.startTime} - ${workshop.endTime}`}
                                        size='small'
                                        variant='outlined'
                                        sx={{ color: 'inherit', borderColor: 'currentColor' }}
                                    />
                                    <Chip
                                        icon={<IconMail size={16} />}
                                        label={workshop.venue}
                                        size='small'
                                        variant='outlined'
                                        sx={{ color: 'inherit', borderColor: 'currentColor' }}
                                    />
                                </Stack>
                            </Box>
                        </AccordionSummary>

                        <AccordionDetails>
                            <Box sx={{ p: 2 }}>
                                {/* Format Toggle */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography
                                        variant='h6'
                                        gutterBottom
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                        <IconCode size={20} />
                                        Email Format
                                    </Typography>
                                    <Stack direction='row' spacing={2} alignItems='center'>
                                        <Typography
                                            variant='body2'
                                            color={!template.isHtmlMode ? 'primary.main' : 'text.secondary'}
                                        >
                                            Rich Text
                                        </Typography>
                                        <Switch
                                            checked={template.isHtmlMode || false}
                                            onChange={e => handleFormatToggle(workshop.id, e)}
                                            color='primary'
                                        />
                                        <Typography
                                            variant='body2'
                                            color={template.isHtmlMode ? 'primary.main' : 'text.secondary'}
                                        >
                                            HTML
                                        </Typography>
                                    </Stack>
                                </Box>

                                {/* Subject Input */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant='h6' gutterBottom>
                                        Email Subject
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        placeholder='Enter email subject (use variables like $ThemeName)'
                                        value={template.subject || ''}
                                        onChange={e =>
                                            setTemplates(prev => ({
                                                ...prev,
                                                [workshop.id]: {
                                                    ...prev[workshop.id],
                                                    subject: e.target.value,
                                                },
                                            }))
                                        }
                                        onFocus={() => setActiveField('subject')}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: 'primary.main',
                                                },
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Body Editor */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant='h6' gutterBottom>
                                        Email Body
                                    </Typography>
                                    {template.isHtmlMode ? (
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={12}
                                            placeholder='<html><body><h1>Hello there!</h1><p>Welcome to $ThemeName!</p><p>QR Code: $QR</p><p>Calendar: $calendar</p></body></html>'
                                            value={template.htmlBody || ''}
                                            onChange={e =>
                                                setTemplates(prev => ({
                                                    ...prev,
                                                    [workshop.id]: {
                                                        ...prev[workshop.id],
                                                        htmlBody: e.target.value,
                                                    },
                                                }))
                                            }
                                            onFocus={() => setActiveField('htmlBody')}
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.875rem',
                                                },
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'primary.main',
                                                    },
                                                },
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}
                                            onClick={() => setActiveField('richTextBody')}
                                        >
                                            <ReactQuill
                                                theme='snow'
                                                value={template.richTextBody || ''}
                                                onChange={value =>
                                                    setTemplates(prev => ({
                                                        ...prev,
                                                        [workshop.id]: {
                                                            ...prev[workshop.id],
                                                            richTextBody: value,
                                                        },
                                                    }))
                                                }
                                                modules={{
                                                    toolbar: [
                                                        [{ header: [1, 2, 3, false] }],
                                                        ['bold', 'italic', 'underline', 'strike'],
                                                        [{ color: [] }, { background: [] }],
                                                        [{ list: 'ordered' }, { list: 'bullet' }],
                                                        ['link', 'image'],
                                                        ['clean'],
                                                    ],
                                                }}
                                                style={{
                                                    minHeight: '300px',
                                                    '& .ql-editor': {
                                                        minHeight: '250px',
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>

                                {/* Action Buttons */}
                                <Stack direction='row' spacing={2} sx={{ mb: 3 }}>
                                    <AnimateButton>
                                        <Button
                                            variant='contained'
                                            startIcon={<Save size={20} />}
                                            onClick={() => handleSave(workshop.id)}
                                            disabled={saving}
                                            sx={{
                                                background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                                                '&:hover': {
                                                    background: 'linear-gradient(45deg, #45a049 30%, #4CAF50 90%)',
                                                },
                                            }}
                                        >
                                            {saving ? 'Saving...' : 'Save Template'}
                                        </Button>
                                    </AnimateButton>

                                    <Button
                                        variant='outlined'
                                        startIcon={<IconSend size={20} />}
                                        onClick={() => {
                                            setSelectedWorkshopId(workshop.id);
                                            setTestEmailDialog(true);
                                        }}
                                        sx={{
                                            borderColor: 'secondary.main',
                                            color: 'secondary.main',
                                            '&:hover': {
                                                borderColor: 'secondary.dark',
                                                bgcolor: 'secondary.light',
                                            },
                                        }}
                                    >
                                        Send Test Email
                                    </Button>
                                </Stack>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                );
            })}

            {/* Test Email Dialog */}
            <Dialog open={testEmailDialog} onClose={() => setTestEmailDialog(false)} maxWidth='sm' fullWidth>
                <DialogTitle>Send Test Confirmation Email</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label='Test Email Address'
                        type='email'
                        value={testEmail}
                        onChange={e => setTestEmail(e.target.value)}
                        placeholder='Enter email address to send test to'
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTestEmailDialog(false)}>Cancel</Button>
                    <Button
                        onClick={() => handleSendTest(selectedWorkshopId)}
                        variant='contained'
                        disabled={testing || !testEmail.trim()}
                        startIcon={<IconSend size={20} />}
                    >
                        {testing ? 'Sending...' : 'Send Test'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Copy notification snackbar */}
            <Snackbar
                open={copySnackbar.open}
                autoHideDuration={3000}
                onClose={() => setCopySnackbar({ ...copySnackbar, open: false })}
            >
                <Alert
                    onClose={() => setCopySnackbar({ ...copySnackbar, open: false })}
                    severity='success'
                    sx={{ width: '100%' }}
                >
                    {copySnackbar.message}
                </Alert>
            </Snackbar>
        </MainCard>
    );
};

export default ConfirmationEmailTemplateManager;
